// ── Welle B3: Mehrere Stunden per Sprache nachtragen ──────────────────────────────
// Die Lehrkraft erzählt mehrere Stunden am Stück. Der Server ordnet jede Aussage einer Stunde
// der letzten 14 Schultage zu (KI schlägt vor, Server prüft: nichts Erfundenes, nur Stunden aus
// dem Stundenplan). Je Stunde eine Karte zum Prüfen; nicht Zuordenbares auf „Welche Stunde?“.
// Gibt es schon einen Eintrag: Ergänzen, Ersetzen oder Überspringen. Gespeichert wird je Karte
// über POST /api/klassenbuch — Fehlende werden wie gewohnt zu Fehlzeiten.
import React, { useEffect, useRef, useState } from 'react';
import { MIcon } from '../../lib/micon';
import { apiPost } from '../../lib/api';
import { toast } from '../../lib/toast';
import { useSchueler } from '../../contexts/SchuelerContext';
import { LdSheet } from '../../components/ui/LdSheet';
import { LdFeld, LdEingabe, LdTextfeld, LdAuswahl, LdFensterFuss } from '../../components/ld/LdFenster';
import '../../components/diktat/diktat-review.css';
import { NACHTRAGEN_EVENT, KLASSENBUCH_GEAENDERT } from '../../lib/nav';
import { useFeldDiktat } from '../schueler/profil/DiktatFeldKnopf';
import { useMitarbeitImEintrag, MitarbeitImEintrag } from './MitarbeitImEintrag';
import './NachtragenSheet.css';

type Vorhanden = { id: number; fach: string; thema: string; hausaufgaben: string; anmerkungen: string; fehlende: number[]; entschuldigte: number[] };
type Inhalt = { thema: string; hausaufgaben: string; anmerkungen: string; fehlende: number[]; fehlende_unklar: string[]; text_quelle: string };
type KarteRoh = Inhalt & { datum: string; stunde_nr: number; klasse_id: number; klasse_name: string; fach: string; vorhanden: Vorhanden | null;
  doppelstunde: { stunde_nr: number; vorhanden: Vorhanden | null } | null };
type Stunde = { datum: string; stunde_nr: number; klasse_id: number; klasse_name: string; fach: string; eingetragen: boolean; vorhanden: Vorhanden | null };
type Ergebnis = { karten: KarteRoh[]; unzugeordnet: Inhalt[]; stunden: Stunde[] };
type Modus = 'ergaenzen' | 'ersetzen' | 'ueberspringen';
type Karte = KarteRoh & { key: string; modus: Modus; mitDoppel: boolean; status: 'offen' | 'gespeichert' | 'verworfen'; fehler: string };

const WT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const tag = (d: string) => `${WT[new Date(d + 'T12:00:00').getDay()]} ${d.slice(8, 10)}.${d.slice(5, 7)}.`;
const stundeText = (s: { datum: string; stunde_nr: number; klasse_name: string; fach: string }) => `${tag(s.datum)} · ${s.stunde_nr}. Std · ${s.klasse_name} ${s.fach}`;
const keyVon = (s: { klasse_id: number; datum: string; stunde_nr: number }) => `${s.klasse_id}|${s.datum}|${s.stunde_nr}`;
const zusammen = (alt: string, neu: string) => (!alt.trim() ? neu : !neu.trim() || alt.trim() === neu.trim() ? alt : `${alt}; ${neu}`);

/** Body für POST /api/klassenbuch je nach Modus (Ergänzen: Leeres füllen, Fehlende vereinigen) */
function body(k: Karte, stundeNr: number, vorh: Vorhanden | null) {
  const basis = { klasse_id: k.klasse_id, datum: k.datum, stunde: stundeNr };
  if (vorh && k.modus === 'ergaenzen') return { ...basis, fach: vorh.fach || k.fach,
    thema: zusammen(vorh.thema, k.thema) || null, hausaufgaben: zusammen(vorh.hausaufgaben, k.hausaufgaben) || null,
    anmerkungen: zusammen(vorh.anmerkungen, k.anmerkungen) || null,
    fehlende_schueler: Array.from(new Set([...vorh.fehlende, ...k.fehlende])), entschuldigte_schueler: vorh.entschuldigte };
  return { ...basis, fach: k.fach, thema: k.thema.trim() || null, hausaufgaben: k.hausaufgaben.trim() || null, anmerkungen: k.anmerkungen.trim() || null,
    fehlende_schueler: k.fehlende, entschuldigte_schueler: (vorh?.entschuldigte ?? []).filter(id => k.fehlende.includes(id)) };
}

export function KlassenbuchNachtragenHost() {
  const [offen, setOffen] = useState(false);
  useEffect(() => {
    const h = () => setOffen(true);
    window.addEventListener(NACHTRAGEN_EVENT, h);
    return () => window.removeEventListener(NACHTRAGEN_EVENT, h);
  }, []);
  return offen ? <NachtragenSheet onClose={() => setOffen(false)} /> : null;
}

function NachtragenSheet({ onClose }: { onClose: () => void }) {
  const { schuelerListe } = useSchueler();
  const [text, setText] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState('');
  const [stunden, setStunden] = useState<Stunde[]>([]);
  const [karten, setKarten] = useState<Karte[] | null>(null);
  const [offeneAussagen, setOffeneAussagen] = useState<(Inhalt & { key: string })[]>([]);
  const speicher = useRef(new Map<string, () => Promise<boolean>>());
  const diktat = useFeldDiktat(t => setText(alt => (alt ? `${alt} ${t}` : t)));

  const kinderVon = (klasseId: number) => schuelerListe.filter(s => Number(s.klasse_id) === Number(klasseId))
    .sort((a, b) => a.name.localeCompare(b.name, 'de')).map(s => ({ id: s.id, name: s.name }));

  const alsKarte = (r: KarteRoh): Karte => ({ ...r, key: keyVon(r), modus: 'ergaenzen', mitDoppel: !!r.doppelstunde && !r.doppelstunde.vorhanden, status: 'offen', fehler: '' });

  async function zuordnen() {
    if (!text.trim()) return;
    setLaeuft(true); setFehler('');
    try {
      const e = await apiPost<Ergebnis>('/api/klassenbuch/nachtragen', { text: text.trim() });
      setStunden(e.stunden);
      setKarten(e.karten.map(alsKarte));
      setOffeneAussagen(e.unzugeordnet.map((u, i) => ({ ...u, key: `u${i}` })));
      speicher.current.clear();
    } catch (err: any) { setFehler(err?.message ?? 'Die Stunden konnten nicht zugeordnet werden.'); }
    finally { setLaeuft(false); }
  }

  // „Welche Stunde?“ → Karte (Namen nur zuordnen, wenn eindeutig; sonst bleiben sie als Text stehen)
  function stundeWaehlen(a: Inhalt & { key: string }, key: string) {
    const s = stunden.find(x => keyVon(x) === key); if (!s) return;
    if (karten?.some(k => k.key === key)) { toast('Für diese Stunde gibt es schon eine Karte – bitte dort ergänzen.', 'info'); return; }
    const kinder = kinderVon(s.klasse_id);
    const fehlende: number[] = [], unklar: string[] = [];
    for (const n of a.fehlende_unklar) {
      const teile = n.toLowerCase().split(/\s+/).filter(t => t.length >= 3);
      const treffer = kinder.filter(k => teile.some(t => k.name.toLowerCase().split(/\s+/).includes(t)));
      if (treffer.length === 1) fehlende.push(treffer[0].id); else unklar.push(n);
    }
    const doppel = stunden.find(x => x.datum === s.datum && x.klasse_id === s.klasse_id && x.fach === s.fach && x.stunde_nr === s.stunde_nr + 1);
    const neu: KarteRoh = { ...a, datum: s.datum, stunde_nr: s.stunde_nr, klasse_id: s.klasse_id, klasse_name: s.klasse_name, fach: s.fach,
      fehlende, fehlende_unklar: unklar, vorhanden: s.vorhanden,
      doppelstunde: doppel ? { stunde_nr: doppel.stunde_nr, vorhanden: doppel.vorhanden } : null };
    setKarten(k => [...(k ?? []), alsKarte(neu)].sort((x, y) => x.datum.localeCompare(y.datum) || x.stunde_nr - y.stunde_nr));
    setOffeneAussagen(l => l.filter(x => x.key !== a.key));
    if (s.eingetragen) toast('Für diese Stunde gibt es schon einen Eintrag – „Ergänzen“ füllt nur Leeres.', 'info');
  }

  const offeneKarten = (karten ?? []).filter(k => k.status === 'offen');
  async function alleUebernehmen() {
    let ok = 0;
    for (const k of offeneKarten) { const fn = speicher.current.get(k.key); if (fn && await fn()) ok++; }
    if (ok) { toast(`${ok} ${ok === 1 ? 'Stunde' : 'Stunden'} eingetragen`, 'success'); window.dispatchEvent(new CustomEvent(KLASSENBUCH_GEAENDERT)); }
  }

  const setzeKarte = (key: string, teil: Partial<Karte>) => setKarten(l => (l ?? []).map(k => (k.key === key ? { ...k, ...teil } : k)));
  const fertig = karten !== null && offeneKarten.length === 0 && offeneAussagen.length === 0;

  return (
    <LdSheet open onClose={onClose} maxWidth={620}
      kopf={{ titel: 'Per Sprache nachtragen', kontext: 'Mehrere Stunden der letzten 14 Schultage am Stück erzählen', icon: 'mic' }}
      fuss={karten === null
        ? <LdFensterFuss onAbbrechen={onClose} haupt="Stunden zuordnen" onHaupt={zuordnen} laden={laeuft} aus={!text.trim() || diktat.zustand !== 'bereit'} />
        : fertig
          ? <LdFensterFuss onAbbrechen={onClose} abbrechen="Schließen" />
          : <LdFensterFuss onAbbrechen={() => { setKarten(null); setOffeneAussagen([]); }} abbrechen="Zurück zum Text"
              haupt={offeneKarten.length === 1 ? 'Übernehmen' : `Alle ${offeneKarten.length} übernehmen`} onHaupt={alleUebernehmen} aus={!offeneKarten.length} />}>
      <div className="ld-nt">
        {karten === null ? (
          <div className="ld-nt__eingabe">
            <p className="ld-nt__beispiel">Zum Beispiel: „Montag 7b Deutsch Kurzgeschichten, Hausaufgabe Seite 45, gefehlt hat Noah. Dienstag 9a Mathe Bruchrechnung.“</p>
            <button type="button" className={`ld-fe-diktat${diktat.zustand === 'aufnahme' ? ' ist-aktiv' : ''}`} disabled={diktat.zustand === 'verarbeitet' || laeuft}
              onClick={() => (diktat.zustand === 'aufnahme' ? diktat.stop() : diktat.start())}>
              <span className="ld-fe-diktat__mic" aria-hidden="true"><MIcon name={diktat.zustand === 'aufnahme' ? 'stop' : 'mic'} size={20} /></span>
              <span>
                <b>{diktat.zustand === 'aufnahme' ? 'Aufnahme beenden' : diktat.zustand === 'verarbeitet' ? 'Wird verschriftet …' : 'Aufnahme starten'}</b>
                <small>{diktat.zustand === 'aufnahme' ? 'Tippen zum Beenden' : 'Oder unten tippen'}</small>
              </span>
            </button>
            <LdFeld label="Was du gesagt hast" hinweis="Du kannst den Text noch ändern.">
              <LdTextfeld aria-label="Was du gesagt hast" rows={5} value={text} onChange={e => setText(e.target.value)} placeholder="Hier tippen oder erzählen" />
            </LdFeld>
            {fehler && <p className="ld-fe-fehler" role="alert">{fehler}</p>}
          </div>
        ) : (
          <div className="ld-nt__karten">
            <h3 className="ld-dr-titel">Vorschläge <span>{offeneKarten.length + offeneAussagen.length}</span></h3>
            <p className="ld-nt__stand">
              {karten.length} {karten.length === 1 ? 'Stunde' : 'Stunden'} erkannt{offeneAussagen.length ? `, ${offeneAussagen.length} ohne Stunde` : ''}. Nicht Gesagtes bleibt leer.
            </p>
            {offeneAussagen.map(a => (
              <article key={a.key} className="ld-nt-karte ld-nt-karte--frage">
                <header>
                  <span className="ld-dr-karte__icon" aria-hidden="true"><MIcon name="help" size={19} /></span>
                  <b>Welche Stunde?</b>
                  <button type="button" className="ld-dr-karte__weg" aria-label="Verwerfen" onClick={() => setOffeneAussagen(l => l.filter(x => x.key !== a.key))}><MIcon name="close" size={18} /></button>
                </header>
                {a.text_quelle && <p className="ld-nt-karte__quelle">„{a.text_quelle}“</p>}
                <LdFeld label="Stunde wählen">
                  <LdAuswahl aria-label="Stunde wählen" value="" placeholder="Stunde der letzten 14 Schultage" onChange={e => stundeWaehlen(a, e.target.value)}>
                    {[...stunden].sort((x, y) => Number(x.eingetragen) - Number(y.eingetragen)).map(s => (
                      <option key={keyVon(s)} value={keyVon(s)}>{stundeText(s)}{s.eingetragen ? ' (schon eingetragen)' : ''}</option>
                    ))}
                  </LdAuswahl>
                </LdFeld>
              </article>
            ))}
            {karten.map(k => (
              <KarteAnsicht key={k.key} k={k} kinder={kinderVon(k.klasse_id)} setze={t => setzeKarte(k.key, t)}
                registriere={fn => { if (fn) speicher.current.set(k.key, fn); else speicher.current.delete(k.key); }} />
            ))}
            {fertig && <p className="ld-nt__fertig"><MIcon name="task_alt" size={20} />Alles erledigt.</p>}
          </div>
        )}
      </div>
    </LdSheet>
  );
}

function KarteAnsicht({ k, kinder, setze, registriere }: {
  k: Karte; kinder: { id: number; name: string }[]; setze: (t: Partial<Karte>) => void; registriere: (fn: (() => Promise<boolean>) | null) => void;
}) {
  const mitarbeit = useMitarbeitImEintrag(k.klasse_id, k.fach, k.datum);
  const [mitarbeitFehler, setMitarbeitFehler] = useState<Record<number, string>>({});
  const [laeuft, setLaeuft] = useState(false);
  const fehlend = new Set(k.fehlende);
  const v = k.vorhanden;
  const wegFehlzeiten = v && k.modus === 'ersetzen' ? v.fehlende.filter(id => !k.fehlende.includes(id)).length : 0;

  async function uebernehmen(): Promise<boolean> {
    if (k.status !== 'offen') return false;
    if (v && k.modus === 'ueberspringen') { setze({ status: 'verworfen' }); return false; }
    setLaeuft(true); setze({ fehler: '' });
    try {
      await apiPost('/api/klassenbuch', body(k, k.stunde_nr, v));
      if (k.mitDoppel && k.doppelstunde) await apiPost('/api/klassenbuch', body(k, k.doppelstunde.stunde_nr, k.doppelstunde.vorhanden));
      const fehl = mitarbeit.aenderungen(fehlend).length ? await mitarbeit.speichern(fehlend) : {};
      setMitarbeitFehler(fehl);
      if (Object.keys(fehl).length) { setze({ fehler: 'Eintrag gespeichert, Mitarbeit einzelner Kinder nicht – bitte prüfen.' }); return false; }
      setze({ status: 'gespeichert' });
      return true;
    } catch (err: any) { setze({ fehler: err?.message ?? 'Nicht gespeichert.' }); return false; }
    finally { setLaeuft(false); }
  }
  const uebernehmenRef = useRef(uebernehmen);
  uebernehmenRef.current = uebernehmen;
  useEffect(() => { registriere(() => uebernehmenRef.current()); return () => registriere(null); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (k.status !== 'offen') return (
    <article className="ld-nt-karte ld-nt-karte--fertig">
      <header><span className="ld-dr-karte__icon" aria-hidden="true"><MIcon name={k.status === 'gespeichert' ? 'check' : 'close'} size={18} /></span><b>{stundeText(k)}</b><span>{k.status === 'gespeichert' ? 'eingetragen' : 'übersprungen'}</span></header>
    </article>
  );

  return (
    <article className="ld-nt-karte" aria-label={stundeText(k)}>
      <header>
        <span className="ld-dr-karte__icon" aria-hidden="true"><MIcon name="menu_book" size={19} /></span>
        <b>{stundeText(k)}{v && <small className="ld-nt-karte__marke">schon eingetragen</small>}</b>
        <button type="button" className="ld-dr-karte__weg" aria-label={`${stundeText(k)} verwerfen`} onClick={() => setze({ status: 'verworfen' })}><MIcon name="close" size={18} /></button>
      </header>
      {k.text_quelle && <p className="ld-nt-karte__quelle">„{k.text_quelle}“</p>}
      {v && (
        <div className="ld-nt-karte__modus" role="group" aria-label="Vorhandener Eintrag">
          {([['ergaenzen', 'Ergänzen'], ['ersetzen', 'Ersetzen'], ['ueberspringen', 'Überspringen']] as [Modus, string][]).map(([m, l]) => (
            <button key={m} type="button" aria-pressed={k.modus === m} className={`ld-mws__chip${k.modus === m ? ' an' : ''}`} onClick={() => setze({ modus: m })}>{l}</button>
          ))}
          <small>{k.modus === 'ergaenzen' ? 'Füllt Leeres, hängt Neues an, Fehlende kommen dazu.'
            : k.modus === 'ersetzen' ? `Ersetzt den Eintrag.${wegFehlzeiten ? ` ${wegFehlzeiten} ${wegFehlzeiten === 1 ? 'Fehlzeit wird' : 'Fehlzeiten werden'} dabei gelöscht.` : ''}`
            : 'Diese Stunde bleibt, wie sie ist.'}</small>
        </div>
      )}
      {(!v || k.modus !== 'ueberspringen') && (<>
        <div className="ld-nt-karte__felder">
          {([['thema', 'Thema'], ['hausaufgaben', 'Hausaufgabe'], ['anmerkungen', 'Anmerkungen']] as [keyof Inhalt, string][]).map(([f, l]) => (
            <LdFeld key={f} label={l} hinweis={v && (v as any)[f] ? `Bisher: ${(v as any)[f]}` : undefined}>
              <LdEingabe aria-label={l} value={String(k[f] ?? '')} onChange={e => setze({ [f]: e.target.value } as Partial<Karte>)} placeholder="nicht gesagt" />
            </LdFeld>
          ))}
        </div>
        <div className="ld-nt__feld"><span>Fehlende</span>
          <div className="ld-nt-karte__kinder">
            {kinder.map(c => (
              <button key={c.id} type="button" aria-pressed={fehlend.has(c.id)} className={`ld-mws__chip${fehlend.has(c.id) ? ' an' : ''}`}
                onClick={() => setze({ fehlende: fehlend.has(c.id) ? k.fehlende.filter(x => x !== c.id) : [...k.fehlende, c.id] })}>
                {fehlend.has(c.id) && <MIcon name="check" size={14} />}{c.name}</button>
            ))}
          </div>
          {k.fehlende_unklar.length > 0 && <small className="ld-nt-karte__unklar">Nicht zugeordnet: {k.fehlende_unklar.join(', ')} — bitte oben antippen.</small>}
        </div>
        {k.doppelstunde && (
          <label className="ld-nt-karte__doppel">
            <input type="checkbox" checked={k.mitDoppel} onChange={e => setze({ mitDoppel: e.target.checked })} />
            Auch für die {k.doppelstunde.stunde_nr}. Stunde eintragen (Doppelstunde){k.doppelstunde.vorhanden ? ' – dort schon ein Eintrag' : ''}
          </label>
        )}
        <MitarbeitImEintrag z={mitarbeit} kinder={kinder} fehlend={fehlend} fehlgeschlagen={mitarbeitFehler} />
      </>)}
      {k.fehler && <p className="ld-nt__fehler" role="alert">{k.fehler}</p>}
      <div className="ld-nt-karte__knoepfe">
        <button type="button" className="ld-dr-knopf ld-dr-knopf--haupt" disabled={laeuft} onClick={async () => {
          if (await uebernehmen()) { toast(`${stundeText(k)} eingetragen`, 'success'); window.dispatchEvent(new CustomEvent(KLASSENBUCH_GEAENDERT)); }
        }}>
          {laeuft ? 'Bitte warten …' : v && k.modus === 'ueberspringen' ? 'Überspringen' : 'Übernehmen'}</button>
      </div>
    </article>
  );
}
